import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import {
  MatNavList,
  MatListItem,
  MatListItemAvatar,
  MatListItemTitle,
  MatListItemLine,
  MatListItemMeta,
} from '@angular/material/list';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { BrandingService } from '@next/core-lib/branding';
import { PageToolbarComponent, SummaryCardComponent } from '@next/core-lib/components';
import { TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { WrapperSiteSidenavActions } from '@next/core-lib/wrapper-site-sidenav';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { Veterinarian } from 'src/app/cycle/models/cycle.model';

import { getVeterianrians } from 'src/app/cycle/store/selectors/cycle.selectors';

import { Barn, VeterinarianSettings } from '../../models/setting.model';
import * as settingActions from '../../store/actions/setting.actions';
import { getBarns, getVeterinarianSettings } from '../../store/selectors/setting.selector';

@Component({
  selector: 'app-barn-overview',
  templateUrl: 'barn-overview.component.html',
  imports: [
    PageToolbarComponent,
    NgIf,
    MatNavList,
    NgFor,
    MatListItem,
    MatIcon,
    MatListItemAvatar,
    MatListItemTitle,
    MatListItemLine,
    MatListItemMeta,
    MatSlideToggle,
    MatButton,
    MatIconButton,
    MatDivider,
    SummaryCardComponent,
    MatFormField,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    FormsModule,
    MatOption,
    AsyncPipe,
    TranslatePipe,
    AgrNumberPipe,
  ],
})
export class BarnOverviewComponent {
  private readonly store = inject<Store<RootState>>(Store);
  readonly brandingService = inject(BrandingService);
  private readonly router = inject(Router);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);

  barns$ = this.store.select(getBarns);
  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);
  veterinarians$: Observable<Veterinarian[]> = this.store.select(getVeterianrians);
  veterinarianSettings$: Observable<VeterinarianSettings> =
    this.store.select(getVeterinarianSettings);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  add() {
    this.router.navigate(['/settings', 'barn', 'add']);
    this.breakpoint.ltLg$.pipe(take(1)).subscribe((isMobile) => {
      if (isMobile) {
        this.store.dispatch(WrapperSiteSidenavActions.closeSidenav());
      }
    });
  }

  onToggleActive(barn: Barn) {
    const lbarn = { ...barn };
    lbarn.isActive = !barn.isActive;
    this.store.dispatch(settingActions.upsertBarn({ barn: lbarn }));
  }

  editBarn(barn: Barn) {
    this.router.navigate(['/settings', 'barn', barn.id]);
  }

  deleteBarn(barn: Barn) {
    if (barn && barn.id) {
      this.store.dispatch(settingActions.deleteBarnWithConfirmation({ barn }));
    }
  }

  changeVeterinarianSelection(selection: MatSelectChange) {
    this.store.dispatch(
      settingActions.upsertVeterinarianSettings({
        veterinarianSettings: {
          id: selection.value,
          name: '',
        },
      }),
    );
  }
}
